import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  MapPin, 
  Building2, 
  Bird, 
  PawPrint, 
  Bug,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  ArrowLeft
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { SHOW_INSTITUTO_BW_BRANDING } from '../lib/branding';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom marker icons by status
const createIcon = (color) => new L.Icon({
  iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${color}.png`,
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const icons = {
  normal: createIcon('green'),
  warning: createIcon('orange'),
  critical: createIcon('red'),
};

export default function BasesMapPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [basesStats, setBasesStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBase, setSelectedBase] = useState(null);

  useEffect(() => {
    fetchBasesStats();
  }, []);

  const fetchBasesStats = async () => {
    try {
      const response = await axios.get(`${API}/api/bases/stats/map`);
      setBasesStats(response.data);
    } catch (error) {
      console.error('Error fetching bases stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-300';
      case 'warning': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      default: return 'bg-green-100 text-green-800 border-green-300';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'critical': return 'Lotado';
      case 'warning': return 'Atenção';
      default: return 'Normal';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'critical': return <AlertTriangle className="w-4 h-4" />;
      case 'warning': return <TrendingUp className="w-4 h-4" />;
      default: return <CheckCircle className="w-4 h-4" />;
    }
  };

  // Calculate map center based on bases
  const getMapCenter = () => {
    const basesWithCoords = basesStats.filter(b => b.latitude && b.longitude);
    if (basesWithCoords.length === 0) return [-23.5505, -46.6333]; // São Paulo default
    
    const avgLat = basesWithCoords.reduce((sum, b) => sum + b.latitude, 0) / basesWithCoords.length;
    const avgLng = basesWithCoords.reduce((sum, b) => sum + b.longitude, 0) / basesWithCoords.length;
    return [avgLat, avgLng];
  };

  const totalAnimals = basesStats.reduce((sum, b) => sum + b.total_animals, 0);
  const totalReleased = basesStats.reduce((sum, b) => sum + b.released, 0);
  const totalDeaths = basesStats.reduce((sum, b) => sum + b.deaths, 0);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-green-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-emerald-100">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {SHOW_INSTITUTO_BW_BRANDING && (
                <img
                  src="/bw-symbol-final.png"
                  alt="Instituto BW"
                  className="h-12 w-12 object-contain"
                />
              )}
              <div className={SHOW_INSTITUTO_BW_BRANDING ? 'border-l border-emerald-200 pl-3' : ''}>
                <h1 className="text-xl font-bold text-emerald-800">Mapa de Bases CRAS</h1>
                <p className="text-xs text-emerald-500">Visão geográfica e capacidade</p>
              </div>
            </div>
            <div className="flex space-x-2">
              <Button onClick={() => navigate('/')} variant="outline">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
              <Button onClick={logout} variant="ghost" className="text-red-600">Sair</Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-l-4 border-l-emerald-500">
            <CardContent className="pt-4">
              <p className="text-sm text-gray-500">Total de Bases</p>
              <p className="text-2xl font-bold text-emerald-700">{basesStats.length}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-4">
              <p className="text-sm text-gray-500">Animais em Tratamento</p>
              <p className="text-2xl font-bold text-blue-700">{totalAnimals}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="pt-4">
              <p className="text-sm text-gray-500">Soltos</p>
              <p className="text-2xl font-bold text-green-700">{totalReleased}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-gray-500">
            <CardContent className="pt-4">
              <p className="text-sm text-gray-500">Óbitos</p>
              <p className="text-2xl font-bold text-gray-700">{totalDeaths}</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Map */}
          <div className="lg:col-span-2">
            <Card className="overflow-hidden">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-emerald-800">
                  <MapPin className="w-5 h-5" />
                  Localização das Bases
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="h-[500px] w-full">
                  <MapContainer 
                    center={getMapCenter()} 
                    zoom={6} 
                    style={{ height: '100%', width: '100%' }}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    {basesStats.filter(b => b.latitude && b.longitude).map((base) => (
                      <Marker 
                        key={base.id}
                        position={[base.latitude, base.longitude]}
                        icon={icons[base.capacity_status] || icons.normal}
                        eventHandlers={{
                          click: () => setSelectedBase(base)
                        }}
                      >
                        <Popup>
                          <div className="p-2 min-w-[200px]">
                            <h3 className="font-bold text-emerald-800">{base.name}</h3>
                            <p className="text-sm text-gray-600">{base.city}, {base.state}</p>
                            <div className="mt-2 space-y-1">
                              <p className="text-sm"><strong>Animais:</strong> {base.total_animals}</p>
                              <p className="text-sm"><strong>Capacidade:</strong> {base.usage_percent}%</p>
                            </div>
                            <Badge className={`mt-2 ${getStatusColor(base.capacity_status)}`}>
                              {getStatusLabel(base.capacity_status)}
                            </Badge>
                          </div>
                        </Popup>
                      </Marker>
                    ))}
                  </MapContainer>
                </div>
              </CardContent>
            </Card>

            {/* Legend */}
            <div className="flex items-center gap-4 mt-3 text-sm">
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                <span>Normal (&lt;70%)</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 bg-yellow-500 rounded-full"></div>
                <span>Atenção (70-90%)</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 bg-red-500 rounded-full"></div>
                <span>Lotado (&gt;90%)</span>
              </div>
            </div>
          </div>

          {/* Bases List */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-emerald-800">
                  <Building2 className="w-5 h-5" />
                  Lista de Bases
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 max-h-[550px] overflow-y-auto">
                {basesStats.map((base) => (
                  <div 
                    key={base.id}
                    className={`p-3 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                      selectedBase?.id === base.id ? 'ring-2 ring-emerald-500' : ''
                    } ${
                      base.capacity_status === 'critical' ? 'bg-red-50 border-red-200' :
                      base.capacity_status === 'warning' ? 'bg-yellow-50 border-yellow-200' :
                      'bg-white border-gray-200'
                    }`}
                    onClick={() => setSelectedBase(base)}
                    data-testid={`base-card-${base.id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-semibold text-gray-900">{base.name}</h3>
                        <p className="text-xs text-gray-500">{base.city}, {base.state}</p>
                      </div>
                      <Badge className={getStatusColor(base.capacity_status)}>
                        {getStatusIcon(base.capacity_status)}
                        <span className="ml-1">{base.usage_percent}%</span>
                      </Badge>
                    </div>

                    {/* Progress bar */}
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                      <div 
                        className={`h-2 rounded-full ${
                          base.capacity_status === 'critical' ? 'bg-red-500' :
                          base.capacity_status === 'warning' ? 'bg-yellow-500' :
                          'bg-green-500'
                        }`}
                        style={{ width: `${Math.min(base.usage_percent, 100)}%` }}
                      ></div>
                    </div>

                    {/* Animal counts */}
                    <div className="flex items-center gap-3 text-xs">
                      <span className="flex items-center gap-1">
                        <Bird className="w-3 h-3 text-blue-600" />
                        {base.by_group.AVE}
                      </span>
                      <span className="flex items-center gap-1">
                        <PawPrint className="w-3 h-3 text-amber-600" />
                        {base.by_group.MAM}
                      </span>
                      <span className="flex items-center gap-1">
                        <Bug className="w-3 h-3 text-green-600" />
                        {base.by_group.REPT}
                      </span>
                      <span className="text-gray-500 ml-auto">
                        Total: <strong>{base.total_animals}</strong>/{base.capacity}
                      </span>
                    </div>
                  </div>
                ))}

                {basesStats.length === 0 && (
                  <p className="text-center text-gray-500 py-4">
                    Nenhuma base cadastrada
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
